import { useState } from "react";
import type { ChangeEvent } from "react";
import { Box, ButtonBase, CircularProgress, Link, Stack, Typography } from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";
import { PATH_DASHBOARD } from "../../../../routes/paths";
import { alpha, useTheme } from "@material-ui/core/styles";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import CloudUploadRoundedIcon from "@material-ui/icons/CloudUploadRounded";
import { useSnackbar } from "notistack5";
import useWebotServer from "../../../../hooks/useWebotServer";
import { serverFailureMessage } from "../../../../utils/webotServerFailure";
import ListingPicker from "../../signatures/ListingPicker";
import type { ListingData } from "../../signatures/ListingPicker";
import type { REST_schema } from "../../../../@types/sharedSchema";
import type { TemplateListingState } from "./useTemplateListing";
import { fixedUrlButtons, templateNeedsPicture, templateOffersListing } from "./templateListing";

type TemplateView = REST_schema.WhatsappTemplateView;

type Props = {
  template: TemplateView;
  state: TemplateListingState;
  /** The template reads the listing (its fields, or a header declared the listing's photo). */
  required: boolean;
  /**
   * ...but can go without one (`!templateRequiresListing`): its fields can be typed and its picture
   * uploaded. Only a listing LINK needs the pick.
   */
  optional?: boolean;
  disabled?: boolean;
};

/** Thumbnails shown before "עוד"; a listing can carry thirty. */
const SHOWN_PICTURES = 11;

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.readAsDataURL(file);
  });

/**
 * "הנכס בהודעה" — the listing picker and the picture the message opens with, the same in every
 * dialog that sends a template. See `templateListing.ts` for why it exists.
 *
 * An IMAGE header's picture is chosen for every send: one of the listing's photos, or an upload. The
 * template's approved picture is NOT offered — Meta approves a header's FORMAT, never its bytes, so
 * that file is only the review sample (Haim, 2026-10-22: "why do you always include this default
 * image?"). A VIDEO header still offers its own file, since a listing has no video to replace it.
 */
export default function TemplateListingSection({ template, state, required, optional = false, disabled = false }: Props) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { whatsappUploadTemplateMedia } = useWebotServer();
  const [uploading, setUploading] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const offersListing = templateOffersListing(template);
  const needsPicture = templateNeedsPicture(template);
  const isVideo = template.headerFormat === "VIDEO";
  const selected = state.headerImageUrl;
  const pictures = showAll ? state.imageChoices : state.imageChoices.slice(0, SHOWN_PICTURES);
  // A button whose address is fixed in the approved template opens the same page on every send.
  const fixedButton = state.listing ? fixedUrlButtons(template)[0] : undefined;

  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const media = await whatsappUploadTemplateMedia({ dataBase64: await readFileAsBase64(file), fileName: file.name, mimeType: file.type });
      state.addUpload(media.url);
    } catch (error) {
      enqueueSnackbar(serverFailureMessage(error, "העלאת הקובץ נכשלה, נסו שוב"), { variant: "error" });
    } finally {
      setUploading(false);
    }
  };

  const tile = (key: string, content: JSX.Element, chosen: boolean, onClick: () => void, label: string) => (
    <ButtonBase
      key={key}
      aria-label={label}
      aria-pressed={chosen}
      disabled={disabled}
      onClick={onClick}
      sx={{
        position: "relative",
        width: 72,
        height: 72,
        borderRadius: 1.5,
        overflow: "hidden",
        border: `2px solid ${chosen ? theme.palette.primary.main : theme.palette.divider}`,
        bgcolor: alpha(theme.palette.grey[500], 0.08)
      }}
    >
      {content}
      {chosen && (
        <CheckCircleRoundedIcon
          sx={{ position: "absolute", top: 3, insetInlineEnd: 3, fontSize: 18, color: "primary.main", bgcolor: "common.white", borderRadius: "50%" }}
        />
      )}
    </ButtonBase>
  );

  const picture = (url: string) => <Box component="img" src={url} alt="" loading="lazy" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />;

  // What the realtor still has to do before this send can go, said where the pictures are.
  const pictureNote = !needsPicture || selected
    ? undefined
    : state.listing && !state.loading && state.imageChoices.length === 0
    ? "לנכס הזה אין תמונות. העלו תמונה כדי לשלוח."
    : state.listing
    ? undefined
    : "בחרו נכס כדי לשלוח אחת התמונות שלו, או העלו תמונה.";

  return (
    <Stack spacing={1.5} sx={{ p: 1.5, borderRadius: 1.5, border: `1px solid ${theme.palette.divider}` }}>
      {offersListing && (
        <>
          <Box>
            <Typography variant="subtitle2">
              {required ? (optional ? "על איזה נכס ההודעה? (לא חובה)" : "על איזה נכס ההודעה?") : "איזו תמונה תופיע בראש ההודעה?"}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
              {required && optional
                ? "בחירת נכס תמלא את הפרטים שלו לבד. אפשר גם לכתוב אותם בעצמכם."
                : required
                ? "פרטי הנכס ייכנסו להודעה אוטומטית, ואפשר לערוך אותם לפני השליחה."
                : "בחרו נכס ואחת התמונות שלו, או העלו תמונה משלכם."}
            </Typography>
          </Box>
          <ListingPicker
            loading={state.loading}
            selectedListing={(state.listing as ListingData | null) ?? null}
            setSelectedListing={(listing) => state.pick(listing)}
          />
          {fixedButton && (
            <Typography variant="caption" sx={{ color: "warning.dark", display: "block" }}>
              הכפתור „{fixedButton.text}" בתבנית הזו מוביל תמיד לאותה כתובת (
              <Box component="span" dir="ltr">
                {fixedButton.url.replace(/^https?:\/\//, "").slice(0, 48)}
              </Box>
              ), ולא לנכס שבחרתם. כדי שיפתח את הנכס, שכפלו את התבנית ב
              <Link component={RouterLink} to={PATH_DASHBOARD.general.whatsappTemplates}>
                תבניות
              </Link>{" "}
              ובחרו "שהכפתור יפתח את הנכס שנבחר בשליחה".
            </Typography>
          )}
        </>
      )}

      {(needsPicture || isVideo) && (
        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.75 }}>
            {isVideo ? "הסרטון בראש ההודעה" : "התמונה בראש ההודעה"}
          </Typography>
          <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
            {needsPicture && pictures.map((url, index) => tile(url, picture(url), selected === url, () => state.setHeaderImageUrl(url), `תמונה ${index + 1} של הנכס`))}
            {needsPicture && state.imageChoices.length > pictures.length && (
              <ButtonBase onClick={() => setShowAll(true)} sx={{ width: 72, height: 72, borderRadius: 1.5, border: `1px dashed ${theme.palette.divider}` }}>
                <Typography variant="caption">{`עוד ${state.imageChoices.length - pictures.length}`}</Typography>
              </ButtonBase>
            )}
            {state.uploads.map((url, index) =>
              tile(
                url,
                isVideo ? <Typography variant="caption">הועלה</Typography> : picture(url),
                selected === url,
                () => state.setHeaderImageUrl(url),
                `הקובץ שהעליתם ${index + 1}`
              )
            )}
            {isVideo &&
              template.headerMediaUrl &&
              tile("template", <Typography variant="caption">של התבנית</Typography>, !selected, () => state.setHeaderImageUrl(undefined), "הסרטון של התבנית")}
            <ButtonBase
              component="label"
              disabled={disabled || uploading}
              sx={{ width: 72, height: 72, borderRadius: 1.5, border: `1px dashed ${theme.palette.divider}`, flexDirection: "column", gap: 0.25 }}
            >
              {uploading ? <CircularProgress size={18} /> : <CloudUploadRoundedIcon sx={{ fontSize: 20, color: "text.secondary" }} />}
              <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.2 }}>
                העלאה
              </Typography>
              <input hidden type="file" accept={isVideo ? "video/mp4" : "image/jpeg,image/png"} onChange={upload} />
            </ButtonBase>
          </Stack>
          {pictureNote && (
            <Typography variant="caption" sx={{ color: "warning.dark", display: "block", mt: 0.75 }}>
              {pictureNote}
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );
}
